/**
 * ZAHL-02d (Betreiberfreigabe 14.10., D5/D6, Soll 5) – Owner-Hinweisseite nach Ende der Lesephase.
 *
 * Ersetzt das gesamte Unternehmer-Layout (keine Navigation, keine Inhalte, kein Export), solange
 * `access_state.reason === "gesperrt_vertragsende"`. Text wortgleich; Buttons „Abo neu abschließen"
 * (bestehender Abo-Checkout, D6 – ohne Betreiber) und „Abmelden". Nach Rückkehr von Stripe
 * (`?checkout=success`) wird GET /company einige Male neu geladen, bis der Webhook den Zugang
 * freigeschaltet hat (dann rendert UnternehmerLayout wieder normal).
 */
import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { checkoutErrorText, startAboCheckout, vertragsendeSperrText } from "@/lib/accountPolicy";

const RELOAD_VERSUCHE = 5;
const RELOAD_INTERVALL_MS = 3000;

export default function VertragsendeSperrseite({ vertragsende, onLogout, onReload }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const checkoutSuccess =
    typeof window !== "undefined" && /[?&]checkout=success(&|$)/.test(window.location.search || "");

  useEffect(() => {
    if (!checkoutSuccess || typeof onReload !== "function") return undefined;
    let n = 0;
    const timer = setInterval(() => {
      n += 1;
      onReload();
      if (n >= RELOAD_VERSUCHE) clearInterval(timer);
    }, RELOAD_INTERVALL_MS);
    return () => clearInterval(timer);
  }, [checkoutSuccess, onReload]);

  const neuAbschliessen = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await startAboCheckout(api);
      if (!res || !res.url) {
        setError(checkoutErrorText(null));
        setBusy(false);
      }
    } catch (e) {
      setError(checkoutErrorText(e));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10" data-testid="vertragsende-sperrseite">
      <div className="w-full max-w-xl rounded-lg border border-slate-200 bg-white p-8 shadow-sm space-y-6">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-red-700 shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h1 className="font-heading text-xl font-semibold text-slate-900">Zugang beendet</h1>
            <p className="text-sm text-slate-700" data-testid="vertragsende-sperrseite-text">
              {vertragsendeSperrText(vertragsende)}
            </p>
          </div>
        </div>
        {checkoutSuccess && (
          <p className="text-sm text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="vertragsende-sperrseite-verarbeitung">
            Ihre Zahlung wird verarbeitet. Der Zugang wird in Kürze freigeschaltet.
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={neuAbschliessen}
            disabled={busy}
            className="inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 transition-colors"
            data-testid="vertragsende-sperrseite-abo-btn"
          >
            Abo neu abschließen
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-100 transition-colors"
            data-testid="vertragsende-sperrseite-logout-btn"
          >
            Abmelden
          </button>
        </div>
        {error && (
          <p className="text-sm text-red-700" role="alert" data-testid="vertragsende-sperrseite-error">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
